import { FloorExceptionalClosingHoursService, IGetFloorExceptionalClosingHoursByFloorIdParameters } from '@jooxter/api';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { FloorExceptionalClosingHoursStaleTimeEnum } from '../staleTimes/floor-exceptional-closing-hours-enum';
import { FloorExceptionalClosingHoursQueryKeys } from '../queryKeys/floorExceptionnalClosingHoursQueryKeys.enum';

export const useFetchFloorExceptionalClosingHours = (id?: number) => {
  const fetchFloorExceptionalClosingHours = () =>
    id ? FloorExceptionalClosingHoursService.getExceptionalClosingHoursById(id) : Promise.resolve(null);

  const {
    data: floorExceptionalClosingHours,
    isError,
    isFetching,
  } = useQuery({
    refetchInterval: 30000,
    queryKey: [FloorExceptionalClosingHoursQueryKeys.GetFloorExceptionalClosingHours, id],
    queryFn: () => fetchFloorExceptionalClosingHours(),
    placeholderData: keepPreviousData,
    staleTime: FloorExceptionalClosingHoursStaleTimeEnum.GetFloorExceptionalClosingHours,
  });

  return { floorExceptionalClosingHours, isError, isFetching };
};

export const useFetchFloorExceptionalClosingHoursByFloorId = (
  options?: IGetFloorExceptionalClosingHoursByFloorIdParameters
) => {
  const fetchFloorExceptionalClosingHours = () =>
    FloorExceptionalClosingHoursService.getFloorExceptionalClosingHoursByFloorId(options);

  const {
    data: floorExceptionalClosingHours,
    isError,
    isFetching,
  } = useQuery({
    refetchInterval: 30000,
    enabled: !!(options?.from && options?.to && options.floorId),
    queryKey: [FloorExceptionalClosingHoursQueryKeys.GetFloorExceptionalClosingHoursByFloorId, options],
    queryFn: () => fetchFloorExceptionalClosingHours(),
    placeholderData: keepPreviousData,
    staleTime: FloorExceptionalClosingHoursStaleTimeEnum.GetFloorExceptionalClosingHoursByFloorId,
  });

  return { floorExceptionalClosingHours, isError, isFetching };
};

export const useInvalidateFloorExceptionalClosingHoursQueries = () => {
  const queryClient = useQueryClient();

  const invalidateFloorExceptionalClosingHoursQueries = useCallback(
    (ids: number[]) => {
      queryClient.invalidateQueries({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        predicate: (query: any): boolean => {
          return (
            query.queryKey[0] === FloorExceptionalClosingHoursQueryKeys.GetFloorExceptionalClosingHoursByFloorId ||
            (query.queryKey[0] === FloorExceptionalClosingHoursQueryKeys.GetFloorExceptionalClosingHours &&
              ids.includes(query.queryKey[1]))
          );
        },
      });
    },
    [queryClient]
  );

  return { invalidateFloorExceptionalClosingHoursQueries };
};
